'use client';

import React from 'react';
import { BuilderBlocks, BuilderElement } from '@builder.io/react';
import { SpacingType, sectionVariants, ColumnCount } from "@/types";
import { SectionWrapper } from '../components/SectionWraper';

interface SectionProps {
  content: BuilderElement[];  
  darkMode?: boolean;
  variant?: sectionVariants;
  spacing?: SpacingType;
  usedInPageNav?: boolean;
  pageNavTitle?: string;
  useContainer?: boolean;
  hasShapeBackground?: boolean;
  themeChangeOnScroll?: boolean;
  builderBlock: BuilderElement;
  attributes?: React.HTMLProps<HTMLElement>;
  // New column configuration
  columnCount?: ColumnCount;
  column2Content?: BuilderElement[];
  column3Content?: BuilderElement[];
  columnGap?: "small" | "medium" | "large";
}

export const Section = ({ 
  content, 
  builderBlock, 
  spacing, 
  useContainer, 
  darkMode = false, 
  variant = 'A', 
  usedInPageNav = false, 
  pageNavTitle = 'Section', 
  themeChangeOnScroll = false, 
  attributes,
  columnCount = "1",
  column2Content,
  column3Content,
  columnGap = "medium",
  hasShapeBackground = false
}: SectionProps) => {
  const containerClass = useContainer ? 'container' : '';
  const sectionClass = hasShapeBackground ? 'bg-position-[25%_0px] lg:bg-position-[75%_0px]' : '';
  const sectionStyle = 
    hasShapeBackground ? {
      backgroundImage: "url('/background-graph.svg')",
      backgroundSize: "auto",
      backgroundRepeat: "no-repeat",
      // backgroundAttachment: "fixed",
    } : {};

  const getGridClasses = (count: ColumnCount, gap: "small" | "medium" | "large") => {
    const gapClasses = {
      small: "gap-4 lg:gap-6",
      medium: "gap-6 lg:gap-8", 
      large: "gap-8 lg:gap-12"
    };
    
    const columnClasses = {
      "1": "grid-cols-1",
      "2": "grid-cols-1 lg:grid-cols-2",
      "3": "grid-cols-1 lg:grid-cols-3"
    };
    
    return `grid ${gapClasses[gap]} ${columnClasses[count]}`;
  };
  
  return (
    <SectionWrapper
      usedInPageNav={usedInPageNav}
      pageNavTitle={pageNavTitle}
      themeChangeOnScroll={themeChangeOnScroll}
      spacing={spacing}
      variant={variant}
      darkMode={darkMode}
      heroSection={false}
      componentName="section"
      attributes={attributes}
      style={sectionStyle}
      className={sectionClass}
    > 
      <div className={containerClass}>
        <div className={getGridClasses(columnCount, columnGap)}>
          {/* Always render first column */}
          <div className="flex flex-col">
            <BuilderBlocks
              parentElementId={builderBlock?.id}
              dataPath="content"
              blocks={content ?? []}
            />
          </div>
          
          {/* Render second column for 2+ columns */}
          {(columnCount === "2" || columnCount === "3") && (
            <div className="flex flex-col">
              <BuilderBlocks
                parentElementId={builderBlock?.id}
                dataPath="column2Content"
                blocks={column2Content ?? []}
              />
            </div>
          )}
          
          {/* Render third column for 3 columns */}
          {columnCount === "3" && (
            <div className="flex flex-col">
              <BuilderBlocks
                parentElementId={builderBlock?.id}
                dataPath="column3Content"
                blocks={column3Content ?? []}
              />
            </div>
          )}
        </div>
      </div>
    </SectionWrapper>
  );
}; 